import {
  createContext,
  useEffect,
  useReducer,
  ReactNode,
  useCallback,
} from 'react';
import getAppVersion from 'api/appVersion';
import { Login } from 'api/authenticate';
import setAuthorizationToken from 'api/setAuthorizationToken';
import { filterDataStore, resetAllStores } from 'src/store';
import { type IUser, DefaultIUser } from 'types/loginResponse';
import { LoginType } from 'src/types/loginData';
import { SnackbarUtils } from 'src/utils/snackbarUtilsConfigurator';
import jwtDecode from 'jwt-decode';
import { SystemClaimPermission } from 'src/types/claims';

interface IPayloadState {
  isAuthenticated: boolean;
  user: IUser;
  version: string;
  loginType: LoginType;
}

interface State extends Omit<IPayloadState, 'user'> {
  isInitialized: boolean;
  user: IUser | null;
}
interface AuthContextValue extends State {
  platform: 'JWT';
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  setLoginType: (loginType: LoginType) => void;
}
interface AuthProviderProps {
  children?: ReactNode;
}

interface InitializeAction {
  type: 'INITIALIZE';
  payload: IPayloadState;
}

interface LoginAction {
  type: 'LOGIN';
  payload: {
    user: IUser;
  };
}

interface LogoutAction {
  type: 'LOGOUT';
}
interface TypeLoginAction {
  type: 'SET_LOGIN_TYPE';
  payload: { loginType: LoginType };
}

type Action = InitializeAction | LoginAction | LogoutAction | TypeLoginAction;

const initialState: State = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  version: '',
  loginType: LoginType.NORMAL,
};

const handlers: Record<string, (state: State, action: Action) => State> = {
  INITIALIZE: (state: State, action: Action): State => {
    const { isAuthenticated, user, version } = (action as InitializeAction)
      .payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
      version,
    };
  },
  LOGIN: (state: State, action: Action): State => {
    const { user } = (action as LoginAction).payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  LOGOUT: (state: State): State => ({
    ...state,
    isAuthenticated: false,
    user: null,
    loginType: LoginType.NORMAL,
  }),
  SET_LOGIN_TYPE: (state: State, action: Action): State => {
    const { loginType } = (action as TypeLoginAction).payload;
    return {
      ...state,
      loginType,
    };
  },
};

const reducer = (state: State, action: Action): State =>
  handlers[action.type](state, action);

const AuthContext = createContext<AuthContextValue>({
  ...initialState,
  platform: 'JWT',
  login: () => Promise.resolve(),
  logout: () => {
    // lala
  },
  setLoginType: () => {
    // lala
  },
});

export const AuthProvider = (props: AuthProviderProps) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialState);
  const resetfilter = filterDataStore((state) => state.reset);

  const initialize = useCallback(async () => {
    const payload: IPayloadState = {
      isAuthenticated: false,
      user: DefaultIUser,
      version: '',
      loginType: state.loginType,
    };

    try {
      //get system permission Token
      const authToken = localStorage.getItem('AccessToken');
      const permissionToken = localStorage.getItem('PermissionToken');

      if (authToken && permissionToken) {
        setAuthorizationToken();
        const decodedToken = jwtDecode<any>(permissionToken);
        let parsedClaims: SystemClaimPermission = {} as SystemClaimPermission;

        parsedClaims =
          typeof decodedToken.claims === 'string'
            ? JSON.parse(decodedToken.claims)
            : decodedToken.claims;

        const userPayload = {
          userName: decodedToken.username,
          name: decodedToken.name,
          email: decodedToken.email,
          rut: decodedToken.rut,
          rol: decodedToken.rol,
          profileName: decodedToken.profileName,
          claims: parsedClaims,
        };
        payload.user = userPayload;
        payload.isAuthenticated = true;
        resetfilter();
      }

      try {
        const responseVersion = await getAppVersion();
        payload.version = responseVersion;
        localStorage.setItem('version', responseVersion);
      } catch (err) {
        SnackbarUtils.error('Ocurrió un error inicializando la aplicación');
        // eslint-disable-next-line no-console
        console.error(err);
      }

      dispatch({
        type: 'INITIALIZE',
        payload: payload,
      });
    } catch (err) {
      dispatch({
        type: 'INITIALIZE',
        payload: payload,
      });
    }
  }, [resetfilter]);

  useEffect(() => {
    initialize().catch((err) => {
      // eslint-disable-next-line no-console
      console.error('Error during authentication initialization:', err);
    });
  }, []);

  const login = async (username: string, password: string): Promise<void> => {
    const response = await Login({ username, password });
    if (typeof response === 'string') throw new Error(response);
    dispatch({
      type: 'LOGIN',
      payload: {
        user: response.user,
      },
    });
  };

  const logout = () => {
    localStorage.removeItem('AccessToken');
    localStorage.removeItem('RefreshToken');
    localStorage.removeItem('PermissionToken');
    dispatch({ type: 'LOGOUT' });
    resetAllStores();
  };

  const setLoginType = (loginType: LoginType) => {
    dispatch({
      type: 'SET_LOGIN_TYPE',
      payload: {
        loginType,
      },
    });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        platform: 'JWT',
        login,
        logout,
        setLoginType,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
